import { CSSProperties, memo, useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Search,
  FileText,
  Award,
  GraduationCap,
  Eye,
  RotateCcw,
  AlertCircle,
  Languages,
  Shield,
} from "lucide-react";
import { notify } from "@/lib/utils/toast";
import {
  publicSearchApi,
  SearchObjectType,
  type SearchResult,
} from "../lib/api/public-search";
import { ViewDetailSearchResultModal } from "./ViewDetailSearchResultModal";
import { format } from "date-fns";
import { PaginationIndex } from "./PaginationIndex";

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryPlaceholder, setSearchQueryPlaceholder] = useState("");
  const [searchType, setSearchType] = useState<"all" | SearchObjectType>("all");
  const [searchBy, setSearchBy] = useState<
    "name" | "cccd" | "studentId" | "number"
  >("name");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [dataForViewDetail, setDataForViewDetail] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, _] = useState(20);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(false);
    setSearchQueryPlaceholder(searchQuery);

    //= Tách chuỗi để loại bỏ khoảng trắng thừa giữa các từ
    let separated = false;
    let trimmedQuery = "";
    searchQuery
      .trim()
      .split("")
      .forEach((char) => {
        if (char != " ") {
          trimmedQuery += char;
          separated = false;
        } else {
          if (separated == false) {
            trimmedQuery += char;
            separated = true;
          }
        }
      });
    //= Kết thúc tách chuỗi để loại bỏ khoảng trắng thừa giữa các từ

    try {
      const result = await publicSearchApi.search({
        query: trimmedQuery.toLocaleLowerCase(),
        searchBy,
        type: searchType === "all" ? undefined : searchType,
        limit: itemsPerPage,
        page: currentPage,
      });

      if (result.success && result.data) {
        setSearchResults(result.data.results);
        setHasSearched(true);
        setTotalCount(result.data.total || 0);
        setTotalPages(Math.ceil((result.data.total || 0) / itemsPerPage) || 1);

        if (result.data.results.length === 0) {
          // notify.info("Không tìm thấy kết quả phù hợp");
        } else {
          notify.success(`Tìm thấy ${result.data.total || 0} kết quả`);
        }
      } else {
        throw new Error(result.message || "Có lỗi xảy ra khi tìm kiếm");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Có lỗi xảy ra khi tìm kiếm";
      setSearchError(errorMessage);
      notify.error("Tìm kiếm thất bại");
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [currentPage]);

  const handleReset = () => {
    setSearchQuery("");
    setSearchResults([]);
    setHasSearched(false);
    setSearchError(null);
    setSearchType("all");
    setSearchBy("name");
    setSearchQueryPlaceholder("");
    setCurrentPage(1);
    notify.success("Đã xóa kết quả tìm kiếm");
  };

  const getCertificateIcon = (type: string) => {
    switch (type) {
      case "bachelor":
      case "master":
        return <GraduationCap className="w-5 h-5" />;
      case "language":
        return <Languages className="w-5 h-5" />;
      case "gdqp":
        return <Shield className="w-5 h-5" />;
      case "award":
        return <Award className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "bachelor":
        return "Văn Bằng Đại học";
      case "master":
        return "Văn Bằng Thạc sĩ";
      case "language":
        return "Chứng chỉ ngoại ngữ";
      case "gdqp":
        return "Chứng chỉ Giáo dục quốc phòng";
      case "scholarship":
        return "Khen thưởng - Chứng nhận";
      case "award":
        return "Khen thưởng";
      default:
        return "";
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
      <div className="text-center space-y-3 sm:space-y-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl text-primary px-4 leading-tight">
          Tra cứu Văn bằng - Chứng chỉ - Khen thưởng
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground px-4">
          Tìm kiếm và xác thực thông tin văn bằng, chứng chỉ, khen thưởng
        </p>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="searchBy" className="text-sm font-medium">
                Tìm kiếm theo
              </Label>
              <Select
                value={searchBy}
                onValueChange={(value: any) => setSearchBy(value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Họ tên</SelectItem>
                  <SelectItem value="cccd">Số CCCD</SelectItem>
                  <SelectItem value="studentId">Mã sinh viên / Số báo danh</SelectItem>
                  <SelectItem value="number">Số hiệu / Số quyết định</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="searchType" className="text-sm font-medium">
                Loại văn bằng
              </Label>
              <Select
                value={searchType}
                onValueChange={(value: any) => { setSearchType(value); setHasSearched(false); }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả</SelectItem>
                  <SelectItem value="bachelor">Bằng Đại học</SelectItem>
                  <SelectItem value="master">Bằng Thạc sĩ</SelectItem>
                  <SelectItem value="language">Chứng chỉ ngoại ngữ</SelectItem>
                  <SelectItem value="gdqp">Chứng chỉ GDQP</SelectItem>
                  <SelectItem value="scholarship">Khen thưởng - Chứng nhận</SelectItem>
                  {/* <SelectItem value='award'>Chứng nhận khen thưởng</SelectItem> */}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Input
              placeholder={
                searchBy === "name"
                  ? "Nhập họ tên..."
                  : searchBy === "cccd"
                    ? "Nhập số CCCD..."
                    : searchBy === "studentId"
                      ? "Nhập Mã sinh viên..."
                      : "Nhập số hiệu / số quyết định..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && !isSearching && handleSearch()
              }
              className="h-11 text-base"
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => { setCurrentPage(1); handleSearch(); }}
                size="sm"
                disabled={!searchQuery.trim() || isSearching}
                className="bg-[#D72134] hover:bg-[#D72134]/80 text-base font-medium cursor-pointer"
              >
                <Search className="w-4 h-4 mr-1" />
                {isSearching ? "Đang tìm..." : "Tìm kiếm"}
              </Button>
              {(hasSearched || searchResults.length > 0 || searchError) && (
                <Button
                  variant="outline"
                  size={"sm"}
                  onClick={handleReset}
                  disabled={isSearching}
                  className="text-base cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Đặt lại</span>
                  <span className="sm:hidden">Làm mới</span>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {searchError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{searchError}</AlertDescription>
        </Alert>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
            <h2 className="text-lg sm:text-xl font-semibold">
              Kết quả tìm kiếm ({totalCount} kết quả)
            </h2>
          </div>
          <div className="flex flex-col gap-4">
            {searchResults.map((cert, index) => (
              <Card key={index} className="border border-[#1F2251]/30">
                <CardHeader className="pb-3 px-4 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getCertificateIcon(cert.type)}
                      </div>
                      <CardTitle className="text-base sm:text-lg leading-snug line-clamp-2">
                        <div style={{ textTransform: "capitalize" }}>
                          {cert.title}
                        </div>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 px-4 sm:px-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 text-sm mb-4">
                    <InfoSection
                      title="Họ tên"
                      content={cert.studentName}
                      style={{ textTransform: "capitalize" }}
                    />
                    <InfoSection
                      title="Ngày sinh"
                      content={cert.originalData.dateOfBirth ? format(
                        new Date(cert.originalData.dateOfBirth),
                        "dd/MM/yyyy"
                      ) : ''}
                    />
                    <InfoSection title={cert.type == "scholarship" ? "Số quyết định" : "Số hiệu"} content={cert.number} />
                    <InfoSection
                      title="Giới tính"
                      content={cert.originalData.gender}
                    />
                    <InfoSection
                      title="Ngành"
                      content={('major' in cert.originalData) ? cert.originalData.major : undefined}
                      style={{ textTransform: "capitalize" }}
                    />
                  </div>
                  <div className="flex justify-end pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDataForViewDetail(cert);
                      }}
                      className="w-full sm:w-auto border-[#1F2251] text-[#1F2251] hover:bg-[#1F2251] hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                      Xem chi tiết
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
          </div>
          <ViewDetailSearchResultModal
            data={dataForViewDetail}
            setDegreeInformation={setDataForViewDetail}
          />
        </div>
      )}

      {/* No Results Found */}
      {hasSearched &&
        searchResults.length === 0 &&
        !isSearching &&
        !searchError ? (
        <Card>
          <CardContent className="p-6 sm:p-8 text-center space-y-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-muted rounded-full flex items-center justify-center">
              <Search className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-lg sm:text-xl font-semibold">
                Không tìm thấy kết quả
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
                Không tìm thấy kết quả nào cho "
                <span className="font-medium">{searchQueryPlaceholder}</span>"{" "}
                {searchType !== "all" && (
                  <span>trong {getTypeLabel(searchType).toLowerCase()}</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="h-[260px]"></div>
      )}
    </div>
  );
}

const InfoSection = memo(
  ({
    title,
    content,
    style,
  }: {
    title: string;
    content?: string;
    style?: CSSProperties;
  }) => {
    if (!content) {
      return <></>;
    }
    return (
      <div className="space-y-1">
        <span className="text-muted-foreground text-xs uppercase tracking-wide">
          {title}
        </span>
        <p className="font-medium" style={style}>
          {content}
        </p>
      </div>
    );
  }
);
